// Which balance funds a buy, and how sell proceeds read.
//
// The mental model this module encodes: frBTC is the app's WORKING CAPITAL.
// Sells fill it, buys drain it first, and wrapping native BTC is only the
// top-up valve. The UI reads in BTC terms throughout — frBTC is 1:1 with BTC
// by construction (the signer mints and burns it against real sats), so a
// proceeds line can honestly say "≈ 0.0012 BTC" as long as it also says the
// asset that actually lands is frBTC.
//
// THE THREE RUNGS OF A BUY
//
// 1. `frbtc` — held frBTC covers the whole buy: one plain swap, no wrap fee.
// 2. `topup` — frBTC is positive but short: wrap ONLY the shortfall and let
//    the held frBTC ride the swap leg (`/alkane/wrap-swap`'s
//    `frbtc_outpoints` + `carried_frbtc`). This rung only exists when the
//    caller says the API supports it (`allowTopup`) — the deployed client can
//    face an older API that ignores the fields, and against that server a
//    shortfall-sized wrap would under-fund the quoted swap. The panel probes
//    by verifying the API echoes `swap_amount_in` and downgrades this flag
//    for the session when it does not.
// 3. `btc` — no usable frBTC (or top-up unavailable): the full amount wraps
//    and the held crumb stays put, with the copy owning that instead of
//    pretending.

import { FRBTC_ID, wrapIn } from './amm'

/// Sats kept back when spending native BTC so the wrap transaction can still
/// pay its network fee. Mirrors the swap panel's MAX reserve.
export const BTC_FEE_RESERVE = 5_000n

type FundingSource = 'frbtc' | 'btc' | 'topup'

/// Below chain dust an frBTC balance cannot fund anything (the contract
/// refuses sub-dust burns and no pool fill this small is meaningful), so the
/// no-amount default must not lean on it — "paying with frBTC" over a
/// 20-base-unit crumb would be a lie the first keystroke exposes. The same
/// floor gates the top-up rung on BOTH sides: the carried frBTC must beat
/// dust to be worth a carrier input, and the wrap leg's signer deposit is
/// non-standard below dust, so the wrapped shortfall may never shrink past it.
const DUST = 546n

export interface BuyFunding {
	/// Which asset the buy sells: held frBTC (plain swap, no wrap fee, one
	/// transaction), native BTC (chained wrap+swap), or both (`topup` — the
	/// shortfall wraps and the held frBTC rides the swap leg).
	source: FundingSource
	/// The largest single buy the decision can fund right now. With the
	/// top-up rung available the two balances genuinely combine; without it a
	/// buy funds from exactly one side.
	spendable: bigint
	/// True when frBTC exists but cannot ride along — the full amount wraps
	/// from BTC and the held frBTC stays put. The UI owes the user a sentence
	/// about that.
	shortfall: boolean
	/// `topup` only: sats the wrap leg deposits (the shortfall)…
	wrapSats?: bigint
	/// …and the held frBTC (base units = sats) the swap sells alongside the
	/// mint. The panel picks the carrier outpoints that cover exactly this.
	carryFrbtc?: bigint
}

/// Native BTC spendable on a buy after the wrap-fee reserve.
export function btcSpendable(btcBalance: bigint): bigint {
	return btcBalance > BTC_FEE_RESERVE ? btcBalance - BTC_FEE_RESERVE : 0n
}

/// Decide the funding source for a buy needing `amountIn` base units of
/// BTC-side input (frBTC base units are sats, so the two compare directly).
/// `amountIn` null means "nothing typed yet" — the default leans frBTC
/// whenever any is held, because that is the cheaper path. `allowTopup`
/// admits the third rung; leave it false against an API that cannot mix held
/// frBTC into a wrap-swap.
export function buyFunding(
	frbtc: bigint,
	btcBalance: bigint,
	amountIn: bigint | null,
	allowTopup = false
): BuyFunding {
	const btcAvail = btcSpendable(btcBalance)
	// With the top-up rung the balances combine (frBTC needs to clear dust to
	// ever ride along); without it the max is the larger single path.
	const spendable =
		allowTopup && frbtc > DUST ? frbtc + btcAvail : frbtc > btcAvail ? frbtc : btcAvail
	if (amountIn === null || amountIn <= 0n) {
		return { source: frbtc > DUST ? 'frbtc' : 'btc', spendable, shortfall: false }
	}
	if (frbtc >= amountIn) {
		return { source: 'frbtc', spendable, shortfall: false }
	}
	if (allowTopup) {
		// Carry as much held frBTC as fits while keeping the wrapped
		// shortfall at or above dust (the signer deposit floor).
		const carry = frbtc < amountIn - DUST ? frbtc : amountIn - DUST
		if (carry > DUST && btcAvail >= amountIn - carry) {
			return {
				source: 'topup',
				spendable,
				shortfall: false,
				wrapSats: amountIn - carry,
				carryFrbtc: carry
			}
		}
	}
	return { source: 'btc', spendable, shortfall: frbtc > 0n }
}

/// The top-up split for an exact-OUT buy that needs `swapIn` frBTC on the
/// swap's input side: how much held frBTC rides along and how many sats the
/// wrap leg deposits to mint the rest (`wrapIn` — ceil, so the mint always
/// covers). Null when the rung is unavailable, nothing useful can ride, or
/// native BTC cannot fund the wrapped remainder — callers then keep the
/// full-wrap path unchanged.
export function topupForSwapInput(
	frbtc: bigint,
	btcBalance: bigint,
	swapIn: bigint,
	allowTopup = false
): { wrapSats: bigint; carryFrbtc: bigint } | null {
	if (!allowTopup || swapIn <= DUST) {
		return null
	}
	// The wrap leg must still mint at least dust, so held frBTC can cover at
	// most everything above that floor.
	const maxCarry = swapIn - DUST
	const carry = frbtc < maxCarry ? frbtc : maxCarry
	if (carry <= DUST) {
		return null
	}
	const wrapSats = wrapIn(swapIn - carry)
	if (wrapSats < DUST || btcSpendable(btcBalance) < wrapSats) {
		return null
	}
	return { wrapSats, carryFrbtc: carry }
}

/// Sats as a plain BTC decimal for the one-line notes: full 8dp precision,
/// trailing zeros trimmed, and never scientific notation.
function btcText(sats: bigint): string {
	const whole = sats / 100_000_000n
	const frac = (sats % 100_000_000n).toString().padStart(8, '0').replace(/0+$/, '')
	return frac === '' ? whole.toString() : `${whole}.${frac}`
}

/// The one-line funding disclosure for a buy. Null when there is nothing
/// worth saying (no wallet, or a plain BTC wrap with no frBTC held — the
/// panel's own wrap copy already covers that).
export function fundingNote(f: BuyFunding, hasWallet: boolean): string | null {
	if (!hasWallet) {
		return null
	}
	if (f.source === 'frbtc') {
		return 'Paying with frBTC — no wrap fee, one transaction'
	}
	if (f.source === 'topup') {
		return `Topping up — wrapping ${btcText(f.wrapSats!)} BTC alongside ${btcText(
			f.carryFrbtc!
		)} frBTC`
	}
	if (f.shortfall) {
		return 'frBTC balance too low for this buy — wrapping BTC for the full amount'
	}
	return 'Wrapping BTC → frBTC first (0.1% fee), then swapping — one action'
}

/// Sell proceeds, denominated in BTC. frBTC base units ARE sats, so the
/// figure needs no conversion — only the honest label that the asset landing
/// in the wallet is frBTC.
export function proceedsInBtc(outId: string, amountOut: bigint): { btc: bigint } | null {
	if (outId !== FRBTC_ID || amountOut <= 0n) {
		return null
	}
	return { btc: amountOut }
}
